import { initializeApp, type FirebaseApp } from "firebase/app";
import { GoogleAuthProvider, getAuth, getRedirectResult, signInWithRedirect, type User } from "firebase/auth";

/**
 * A deployment secret pasted with its surrounding quotes keeps them: the build
 * embeds `"sodhera-search.firebaseapp.com"`, quotes included, and every Firebase
 * URL then points at a hostname no server can resolve. The value is still a
 * non-empty string, so nothing looks wrong until sign-in fails.
 */
export function readSetting(value: string | undefined): string {
  return (value ?? "").trim().replace(/^(["'])(.*)\1$/su, "$2").trim();
}

const config = {
  apiKey: readSetting(import.meta.env.VITE_FIREBASE_API_KEY),
  authDomain: readSetting(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
  projectId: readSetting(import.meta.env.VITE_FIREBASE_PROJECT_ID),
  storageBucket: readSetting(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET),
  messagingSenderId: readSetting(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID),
  appId: readSetting(import.meta.env.VITE_FIREBASE_APP_ID)
};

export function isFirebaseConfigured(): boolean {
  return Boolean(config.apiKey && config.authDomain && config.projectId && config.appId);
}

let app: FirebaseApp | null = null;

function auth() {
  if (!isFirebaseConfigured()) throw new Error("Google sign-in is not configured yet.");
  app ??= initializeApp(config);
  return getAuth(app);
}

function googleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  provider.addScope("email");
  provider.addScope("profile");
  return provider;
}

/**
 * Redirect, not popup. The desktop app opens Safari deliberately, and Safari's
 * tracking protection routinely breaks the popup's handoff back to the opener
 * after Google has already authenticated the user. A redirect keeps the whole
 * flow in one window, and the sign-in link's query parameters survive it.
 */
export async function startGoogleSignIn(): Promise<void> {
  await signInWithRedirect(auth(), googleProvider());
}

/** The user returning from Google, or null on a first visit. */
export function resolvedGoogleUser(redirectUser: User | null, restoredUser: User | null): User | null {
  return redirectUser ?? restoredUser;
}

/** The user returning from Google, or null on a first visit. */
export async function completeGoogleSignIn(): Promise<User | null> {
  const firebaseAuth = auth();
  const result = await getRedirectResult(firebaseAuth);
  // Safari can restore the Firebase user before its redirect result becomes
  // observable. The restored user is the same authenticated Firebase session;
  // use it so the one-time desktop pairing request can still complete.
  return resolvedGoogleUser(result?.user ?? null, firebaseAuth.currentUser);
}

export async function tokenFor(user: User): Promise<string> {
  return user.getIdToken();
}
